import { useEffect, useMemo, useRef, useState } from "react";
import { FormRow } from "components/ui/form";
import { customStyles } from "utils/ui/uiHelpers";
import { ErrorMessage } from "@hookform/error-message";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { DefaultRoundSettings, Option, PlayerUser } from "Models";
import {
  AutoConcede,
  AutoContinue,
  AutoGimme,
  GimmeDistance,
  Ground,
  HandicapMode,
  HandicapPercentage,
  NoPuttMode,
  PinType,
  ScoringType,
  Sky,
  TimeOfDay,
  WindDirection,
  WindSpeed,
} from "types/enums";
import {
  roundConfigEnumsAsReactSelectOptions,
  trueFalseOptions,
} from "utils/enums/enumHelper";
import Label from "components/ui/form/Label";
import Tooltip from "@/components/common/Tooltip";
import { Tooltip as ReactTooltip } from "react-tooltip";
import TeamManagement from "./TeamManagement";
import GearIcon from "components/svg/GearIcon";
import StablefordOptions from "./StablefordOptions";
import FacilityCourseAndHoles from "./facility/FacilityCourseAndHoles";
import SingleSimCourseAndHoles from "./single-sim/SingleSimCourseAndHoles";
import { TbGolf } from "react-icons/tb";
import { useFacilityDefaultRoundSettings } from "modules/default-round-settings";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import { ClubhouseEvents } from "utils/pendo/pendoHelpers";
import { pendoTrackEventWithProperties } from "utils/pendo/pendoHelpers";
import type { RootState } from "StoreTypes";
import { useSelector } from "react-redux";
import { getSimulatorBySimId } from "modules/simulator/simulatorSelectors";
import { GrScorecard } from "react-icons/gr";
import { SelectFormField } from "@/components/common";
import { EnumSelect } from "@/components/common/EnumSelect";
import { firstEnumOptionValue } from "@/utils/object";
import { titleCase } from "text-case";
import { useSelectedQueueItem } from "@/modules/simulator-reservations/simulatorReservationHooks";

const RoundSpecificForm: React.FC<RoundSpecificFormProps> = (props) => {
  const { control, watch, formState, getValues, setValue } = useFormContext();
  const {
    defaultRoundSettings,
    allowPlayerProfileTeeType: allowSelectingProfileTeeType = false,
    roundConfig = false,
    defaultRoundSettingsChecked = true,
    isLeague = false,
  } = props;
  const {
    noPuttMode,
    allowProfileTee,
    updatedAdHocUi,
    allowTerrainEffectFromAdhoc,
  } = useFlags();

  const currentFacilityId = useGetCurrentLocalFacilityId();
  const selectedQueueItem = useSelectedQueueItem(currentFacilityId);
  const [usePlayerProfileTeeType] = useState(true);
  const [useFacilityDefaults, setUseFacilityDefaults] = useState(
    defaultRoundSettingsChecked,
  );
  const [currentFacilityDefaultRoundSettings] =
    useFacilityDefaultRoundSettings(currentFacilityId);
  const simulator = useSelector((state: RootState) =>
    getSimulatorBySimId(state, { simId: props.simulatorId ?? "" }),
  );

  const watchAll = watch();

  const roundInfoPlayers = useWatch({
    control,
    name: "roundInfo.players",
  }) as unknown as PlayerUser[] | undefined;
  const roundInfoPlayerCount = roundInfoPlayers?.length ?? 0;

  const allPlayersHaveTeeType = roundConfig
    ? true
    : watchAll.roundInfo?.players?.every(
        (player: PlayerUser) => player.teeType !== undefined,
      );

  const doesScoringTypeIncludeField = (
    scoringType: string | undefined,
    fieldName: string,
  ): boolean => {
    if (!scoringType) return false;
    const scoringTypesAndFields: { [key: string]: Array<string> } = {
      STROKE: ["handicapMode"],
      MATCH: ["teams", "scotchFoursome", "handicapMode"],
      SCRAMBLE: ["mulligans", "teams"],
      STABLEFORD: ["handicapMode", "stableford"],
    };

    return scoringTypesAndFields[scoringType]?.includes(fieldName) ?? false;
  };
  function handleUseFacilityDefaults() {
    if (!currentFacilityDefaultRoundSettings) return;

    Object.entries(currentFacilityDefaultRoundSettings).forEach(
      ([name, value]) => {
        if (name !== "id" && name !== "facilityId" && name !== "autoContinue") {
          setValue(`roundInfo.${name}`, value);
        }
        if (name === "autoContinue") {
          setValue("autoContinue", value);
        }
      },
    );
  }

  // The condition and automation controls display `defaultRoundSettings` as a
  // fallback but only write to form state on change, so an operator who accepts
  // what they see saves undefined for every one of them. The "use facility
  // defaults" checkbox is what normally commits them — and `isLeague` hides it,
  // leaving no way to do so. Commit them once here instead, so a preset created
  // from the league tray carries the settings the form is already showing.
  //
  // Once only, and only before the operator has touched anything: re-running
  // would overwrite their edits with the facility's values.
  const seededLeagueDefaults = useRef(false);
  useEffect(() => {
    if (!props.isLeague) return;
    if (seededLeagueDefaults.current) return;
    if (!currentFacilityDefaultRoundSettings) return;
    seededLeagueDefaults.current = true;
    handleUseFacilityDefaults();
    // handleUseFacilityDefaults closes over setValue and the settings; the ref
    // guard is what keeps this to a single run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isLeague, currentFacilityDefaultRoundSettings]);

  const scoringTypeOptions = useMemo(
    () =>
      roundConfigEnumsAsReactSelectOptions.scoringType.map((opt) => ({
        ...opt,
        label:
          roundInfoPlayerCount <= 1 &&
          (opt.value === ScoringType.MATCH ||
            opt.value === ScoringType.SCRAMBLE)
            ? `${opt.label} (needs 2+ players)` // example
            : opt.label,
      })),
    [roundInfoPlayerCount],
  );

  useEffect(() => {
    if (
      watchAll?.id &&
      watchAll?.id !== currentFacilityDefaultRoundSettings?.id
    ) {
      setUseFacilityDefaults(false);
    }
  }, [currentFacilityDefaultRoundSettings?.id, watchAll?.id]);

  return (
    <div>
      {!isLeague && (
        <div className="pt-2">
          {props.simulatorId ? (
            <SingleSimCourseAndHoles simulatorId={props.simulatorId} />
          ) : (
            <FacilityCourseAndHoles />
          )}
        </div>
      )}
      {allowTerrainEffectFromAdhoc && !isLeague && (
        <FormRow>
          <Label
            name="Terrain Effects"
            tooltipText="The wait time between finishing the current hole and moving on to the next."
          />
          <label className="text-xs brand-dark-gray poppins dark:text-gray-200">
            <Controller
              name="roundInfo.enableTerrainEffects"
              control={control}
              defaultValue={
                getValues("roundInfo.enableTerrainEffects")
                  ? getValues("roundInfo.enableTerrainEffects")
                  : (simulator?.status.terrainEffectsOn ?? false)
              }
              render={({ field }) => (
                <input
                  {...field}
                  type="checkbox"
                  className="mr-2 rounded border border-gray-400 text-brandGreen focus:ring-brandGreen"
                  checked={field.value}
                  onChange={(e) => {
                    e.target.checked &&
                      pendoTrackEventWithProperties(
                        ClubhouseEvents.CONTROL_UPDATE_TERRAIN_EFFECTS,
                        {
                          value: "true",
                        },
                      );
                    setValue(
                      "roundInfo.enableTerrainEffects",
                      e.target.checked,
                    );
                  }}
                />
              )}
            />
            Enable Terrain Effects
          </label>
        </FormRow>
      )}
      {!isLeague && (
        <div className="bg-gray-100 dark:bg-zinc-800 -mx-12 px-12 mt-8 pb-8 mb-4 pt-2">
          <FormRow>
            <div className="flex flex-row">
              <div className="flex-inline w-auto text-sm brand-dark-gray poppins dark:text-gray-200 flex items-center">
                <TbGolf className="size-5 mr-1" /> Select Tee Options
              </div>
              <Tooltip
                key={`round-configuration-select-tippy`}
                className="z-[9999] w-full max-w-[800px] bg-gray-50 dark:bg-zinc-900 border-2 p-2 border-gray-200 rounded-lg"
                arrow={true}
                placement="auto"
                interactive={true}
                safeBorder={15}
                delay={[750, 0]}
                content={
                  <div className="p-2 flex flex-col w-full max-h-[500px] overflow-y-auto text-xs dark:text-gray-50">
                    When checked, each player will play from the tee specified
                    in their profile. If a profile tee is not set, the default
                    tee for their gender (e.g., Blue for men, Red for women)
                    will be used.
                  </div>
                }
              >
                <div className="inline items-center cursor-pointer border-b border-dashed border-blue-500 text-xs ml-4 mb-0.5 dark:text-gray-50">
                  What&apos;s this?
                </div>
              </Tooltip>
            </div>
          </FormRow>
          {allPlayersHaveTeeType &&
            allowSelectingProfileTeeType &&
            allowProfileTee && (
              <>
                <div className="pt-2">
                  <label className="text-xs brand-dark-gray poppins dark:text-gray-200">
                    <Controller
                      name="roundInfo.usePlayersMemberTee"
                      control={control}
                      defaultValue={
                        getValues("roundInfo.usePlayersMemberTee")
                          ? getValues("roundInfo.usePlayersMemberTee")
                          : false
                      }
                      render={({ field }) => (
                        <input
                          {...field}
                          type="checkbox"
                          className="mr-2 rounded border border-gray-400 text-brandGreen focus:ring-brandGreen"
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            // usePlayersTee should be considered obsolete
                            setValue(
                              "roundInfo.usePlayersTee",
                              e.target.checked,
                            ); //TODO: remove this when updating the models to the new RoundController class
                            setValue(
                              "roundInfo.usePlayersMemberTee",
                              e.target.checked,
                            ); //TODO: remove this when updating the models to the new RoundController class
                            e.target.checked &&
                              pendoTrackEventWithProperties(
                                ClubhouseEvents.CONTROL_UPDATE_PLAYER_PROFILE_TEE_TYPE,
                                {
                                  value: "true",
                                },
                              );
                          }}
                        />
                      )}
                    />
                    Use player profile for tee type
                  </label>
                </div>
              </>
            )}
          {(!usePlayerProfileTeeType ||
            !allowSelectingProfileTeeType ||
            !allowProfileTee ||
            !watch("roundInfo.usePlayersMemberTee")) && (
            <>
              <FormRow header="Mens Tee">
                <Controller
                  name="roundInfo.mensTee"
                  control={control}
                  rules={{ required: "Mens Tee is required" }}
                  defaultValue={
                    getValues("roundInfo.mensTee")
                      ? getValues("roundInfo.mensTee")
                      : (defaultRoundSettings?.mensTee ??
                        firstEnumOptionValue(
                          roundConfigEnumsAsReactSelectOptions?.teeType,
                        ))
                  }
                  render={({ field }) => (
                    <EnumSelect
                      field={field}
                      options={roundConfigEnumsAsReactSelectOptions.teeType}
                      styles={customStyles}
                    />
                  )}
                />
                <ErrorMessage
                  errors={formState.errors}
                  name="roundInfo.mensTee"
                  render={({ message }) => (
                    <div className="text-sm text-red-500">{message}</div>
                  )}
                />
              </FormRow>
              <FormRow header="Womens Tee">
                <Controller
                  name="roundInfo.womensTee"
                  control={control}
                  rules={{ required: "Womens Tee is required" }}
                  defaultValue={
                    getValues("roundInfo.womensTee")
                      ? getValues("roundInfo.womensTee")
                      : (defaultRoundSettings?.womensTee ??
                        firstEnumOptionValue(
                          roundConfigEnumsAsReactSelectOptions?.teeType,
                        ))
                  }
                  render={({ field }) => (
                    <EnumSelect
                      field={field}
                      options={roundConfigEnumsAsReactSelectOptions.teeType}
                      styles={customStyles}
                    />
                  )}
                />
                <ErrorMessage
                  errors={formState.errors}
                  name="roundInfo.womensTee"
                  render={({ message }) => (
                    <div className="text-sm text-red-500">{message}</div>
                  )}
                />
              </FormRow>
            </>
          )}
        </div>
      )}
      <div>
        <div className="pt-4 text-sm text-primary poppins dark:text-gray-200 items-center align-left w-full m-0">
          <GearIcon class="size-4 mr-1" /> Round Options
        </div>
        {updatedAdHocUi && !selectedQueueItem?.id && !isLeague && (
          <div className="py-2 mb-2">
            <label className="text-xs brand-dark-gray poppins dark:text-gray-200">
              <input
                checked={useFacilityDefaults}
                onChange={(e) => {
                  handleUseFacilityDefaults();
                  setUseFacilityDefaults(!useFacilityDefaults);

                  e.target.checked &&
                    pendoTrackEventWithProperties(
                      ClubhouseEvents.CONTROL_UPDATE_USE_FACILITY_DEFAULTS,
                      {
                        value: "true",
                      },
                    );
                }}
                type="checkbox"
                className="mr-2 rounded border border-gray-400 text-brandGreen focus:ring-brandGreen"
              />
              Use default settings
            </label>
          </div>
        )}
      </div>
      {(!useFacilityDefaults || !updatedAdHocUi || selectedQueueItem?.id) && (
        <div
          className={
            props.singleColumn
              ? "grid grid-cols-1 gap-4"
              : "grid grid-cols-2 gap-4"
          }
        >
          {!isLeague && (
            <FormRow
              header="Minutes at range before round?"
              fontSize="mt-2 mb-2 text-sm dark:text-gray-50"
            >
              <Controller
                name="rangeTime"
                control={control}
                defaultValue={getValues("rangeTime") || 0}
                render={({ field }) => (
                  <input
                    {...field}
                    className="border-2 border-gray-300 rounded-lg dark:text-black w-full px-4 py-2"
                    type="number"
                    min={0}
                    step={1}
                  />
                )}
              />
            </FormRow>
          )}
          <FormRow>
            <SelectFormField<PinType>
              label="Pin Type"
              options={roundConfigEnumsAsReactSelectOptions.pinType}
              value={
                watch("roundInfo.pinType") ?? defaultRoundSettings?.pinType
              }
              onValueChange={(value) =>
                setValue("roundInfo.pinType", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.pinType"}
            />
          </FormRow>
          <FormRow>
            <SelectFormField<AutoConcede>
              label="Auto Concede"
              options={roundConfigEnumsAsReactSelectOptions.autoConcede}
              value={
                watch("roundInfo.autoConcede") ??
                defaultRoundSettings?.autoConcede
              }
              onValueChange={(value) =>
                setValue("roundInfo.autoConcede", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.autoConcede"}
              tooltip="The number of shots before you automatically move on to the next hole"
              showError={false}
            />
          </FormRow>
          <FormRow>
            <SelectFormField<AutoContinue>
              label="Auto Continue"
              options={roundConfigEnumsAsReactSelectOptions.autoContinue}
              value={
                watch("autoContinue") ?? defaultRoundSettings?.autoContinue
              }
              onValueChange={(value) =>
                setValue("autoContinue", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"autoContinue"}
              tooltip="The wait time between finishing the current hole and moving on to the next."
              showError={false}
            />
          </FormRow>
          <FormRow>
            <SelectFormField<string | number | boolean>
              label="Auto Drop"
              options={roundConfigEnumsAsReactSelectOptions.autoDrop}
              value={
                watch("roundInfo.autoDrop") ?? defaultRoundSettings?.autoDrop
              }
              onValueChange={(value) =>
                setValue("roundInfo.autoDrop", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.autoDrop"}
              showError={false}
            />
          </FormRow>
          {!isLeague && (
            <FormRow
              fontSize="mt-2 mb-2 text-sm dark:text-gray-50"
              hidden={!noPuttMode || watch("build") < 1876}
              header="No Putt Mode"
              tooltip="When No-Putt mode is on, your players will not be putting. It differs from Gimmie’s in that as soon as the ball lands on the green, they will be assigned a putt score based on the distance to the hole, and play will continue to the next shot. Note, this is only available for Stroke play."
            >
              <Controller
                name="roundInfo.noPuttMode"
                control={control}
                defaultValue={
                  getValues("roundInfo.strokeSettings.noPuttMode") ||
                  getValues("roundInfo.noPuttMode") ||
                  defaultRoundSettings?.noPuttMode ||
                  firstEnumOptionValue(
                    roundConfigEnumsAsReactSelectOptions?.noPuttMode,
                  )
                }
                render={({ field }) => (
                  <EnumSelect
                    field={field}
                    closeMenuOnSelect={true}
                    options={roundConfigEnumsAsReactSelectOptions.noPuttMode}
                    onValueChange={(value) => {
                      if (value === NoPuttMode.ON) {
                        setValue(
                          "roundInfo.gimmeDistance",
                          firstEnumOptionValue(
                            roundConfigEnumsAsReactSelectOptions.gimmeDistance,
                          ),
                        );
                        setValue(
                          "roundInfo.autoGimmes",
                          firstEnumOptionValue(
                            roundConfigEnumsAsReactSelectOptions.autoGimme,
                          ),
                        );
                        setValue(
                          "roundInfo.strokeSettings.noPuttMode",
                          NoPuttMode.ON,
                        );
                      } else {
                        setValue(
                          "roundInfo.strokeSettings.noPuttMode",
                          NoPuttMode.OFF,
                        );
                      }
                    }}
                    styles={customStyles}
                    className="disabled:cursor-not-allowed disabled:bg-gray-100"
                    id="noPuttMode"
                    menuPlacement="auto"
                    isDisabled={
                      watchAll.roundInfo?.scoringType
                        ? watchAll.roundInfo?.scoringType !== ScoringType.STROKE
                        : false
                    }
                  />
                )}
              />
            </FormRow>
          )}
          <FormRow hidden={watch("roundInfo.noPuttMode") === NoPuttMode.ON}>
            <SelectFormField<GimmeDistance>
              label="Gimme Distance"
              options={roundConfigEnumsAsReactSelectOptions.gimmeDistance}
              value={
                watch("roundInfo.gimmeDistance") ??
                defaultRoundSettings?.gimmeDistance
              }
              onValueChange={(value) =>
                setValue("roundInfo.gimmeDistance", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.gimmeDistance"}
              tooltip="The distance from the pin at which an auto gimme is received"
              showError={false}
            />
          </FormRow>
          {watchAll.roundInfo?.gimmeDistance ||
          defaultRoundSettings?.gimmeDistance !== GimmeDistance.OFF ? (
            <FormRow hidden={watch("roundInfo.noPuttMode") === NoPuttMode.ON}>
              <SelectFormField<AutoGimme>
                label="Auto Gimme"
                options={roundConfigEnumsAsReactSelectOptions.autoGimme}
                value={
                  watch("roundInfo.autoGimmes") ??
                  defaultRoundSettings?.autoGimmes
                }
                onValueChange={(value) =>
                  setValue("roundInfo.autoGimmes", value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                fieldName={"roundInfo.autoGimmes"}
                tooltip="The number of shots a 'gimme' costs a player"
                showError={false}
              />
            </FormRow>
          ) : null}
          <FormRow>
            <SelectFormField<Ground>
              label="Ground Conditions"
              options={roundConfigEnumsAsReactSelectOptions.groundConditions}
              value={
                watch("roundInfo.groundConditions") ??
                defaultRoundSettings?.groundConditions
              }
              onValueChange={(value) =>
                setValue("roundInfo.groundConditions", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.groundConditions"}
              tooltip="The condition of the ground"
            />
          </FormRow>
          <FormRow>
            <SelectFormField<string | number | boolean>
              label="Green Stimp"
              options={roundConfigEnumsAsReactSelectOptions.stimpSetting}
              value={
                watch("roundInfo.stimpSetting") ??
                defaultRoundSettings?.stimpSetting
              }
              onValueChange={(value) =>
                setValue("roundInfo.stimpSetting", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.stimpSetting"}
            />
            {/* <ErrorMessage
              errors={formState.errors}
              name="roundInfo.stimpSetting"
              render={({ message }) => (
                <div className="text-sm text-red-500">{message}</div>
              )}
            /> */}
          </FormRow>

          <FormRow>
            <SelectFormField<Sky>
              label="Sky Conditions"
              options={roundConfigEnumsAsReactSelectOptions.skyConditions}
              value={
                watch("roundInfo.skyConditions") ??
                defaultRoundSettings?.skyConditions
              }
              onValueChange={(value) =>
                setValue("roundInfo.skyConditions", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.skyConditions"}
            />
            {/* <ErrorMessage
              errors={formState.errors}
              name="roundInfo.skyConditions"
              render={({ message }) => (
                <div className="text-sm text-red-500">{message}</div>
              )}
            /> */}
          </FormRow>
          <FormRow>
            <SelectFormField<TimeOfDay>
              label="Time of Day"
              options={roundConfigEnumsAsReactSelectOptions.timeOfDay}
              value={
                watch("roundInfo.timeConditions") ??
                defaultRoundSettings?.timeConditions
              }
              onValueChange={(value) =>
                setValue("roundInfo.timeConditions", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.timeConditions"}
              showError={false}
            />
          </FormRow>
          <FormRow>
            <SelectFormField<WindDirection>
              label="Wind Direction"
              options={roundConfigEnumsAsReactSelectOptions.windDirection}
              value={
                watch("roundInfo.windDirection") ??
                defaultRoundSettings?.windDirection
              }
              onValueChange={(value) =>
                setValue("roundInfo.windDirection", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.windDirection"}
              showError={false}
            />
          </FormRow>
          <FormRow>
            <SelectFormField<WindSpeed>
              label="Wind Speed"
              options={roundConfigEnumsAsReactSelectOptions.windSpeed}
              value={
                watch("roundInfo.windSpeed") ?? defaultRoundSettings?.windSpeed
              }
              onValueChange={(value) =>
                setValue("roundInfo.windSpeed", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.windSpeed"}
            />
          </FormRow>
        </div>
      )}
      <div className="col-span-2 border-t border-gray-300 dark:border-gray-700 pt-4 mt-4">
        <div className="grid columns-2 gap-4">
          <div className="col-span-2 my-2">
            <span className="text-sm block text-primary poppins dark:text-gray-50">
              <GrScorecard className="size-4 mr-1" /> Scoring Options
            </span>
          </div>
          {!isLeague && (
            <div>
              <SelectFormField<ScoringType>
                label="Scoring Type"
                menuPlacement="auto"
                options={scoringTypeOptions}
                isOptionDisabled={(option: Option<ScoringType>) => {
                  if (roundInfoPlayerCount > 1) return false;
                  return (
                    option.value === ScoringType.MATCH ||
                    option.value === ScoringType.SCRAMBLE
                  );
                }}
                value={
                  watch("roundInfo.scoringType") ??
                  defaultRoundSettings?.scoringType
                }
                onValueChange={(value) => {
                  setValue("roundInfo.scoringType", value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  if (value !== ScoringType.STROKE) {
                    setValue(
                      "roundInfo.strokeSettings.noPuttMode",
                      NoPuttMode.OFF,
                    );
                    setValue("roundInfo.noPuttMode", NoPuttMode.OFF);
                  }
                }}
                fieldName={"roundInfo.scoringType"}
                className="disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            </div>
          )}
          {doesScoringTypeIncludeField(
            ScoringType[
              watchAll.roundInfo?.scoringType ||
                defaultRoundSettings?.scoringType
            ],
            "stableford",
          ) &&
            watchAll.roundInfo?.scoringType !== ScoringType.STROKE && (
              <StablefordOptions />
            )}

          <div className="w-full col-span-2">
            <SelectFormField<string | number | boolean>
              label="Allow Mulligans"
              options={trueFalseOptions}
              value={
                watch("roundInfo.mulligans") ?? defaultRoundSettings?.mulligans
              }
              onValueChange={(value) =>
                setValue("roundInfo.mulligans", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              fieldName={"roundInfo.mulligans"}
              showError={false}
              className="w-full"
            />
          </div>

          {doesScoringTypeIncludeField(
            ScoringType[
              watchAll.roundInfo?.scoringType ||
                defaultRoundSettings?.scoringType
            ],
            "scotchFoursome",
          ) && (
            <FormRow header="Scotch Foursome" fontSize="text-sm">
              <Controller
                name="roundInfo.matchSettings.scotchFoursome"
                defaultValue={
                  getValues("roundInfo.matchSettings.scotchFoursome")
                    ? getValues("roundInfo.matchSettings.scotchFoursome")
                    : false
                }
                control={control}
                render={({ field }) => (
                  <EnumSelect
                    field={field}
                    closeMenuOnSelect={true}
                    options={trueFalseOptions}
                    styles={customStyles}
                    className="disabled:cursor-not-allowed disabled:bg-gray-100"
                    id="roundAutoDrop"
                  />
                )}
              />
            </FormRow>
          )}
        </div>
        <div className="mt-2 grid columns-2 gap-4">
          {doesScoringTypeIncludeField(
            ScoringType[watchAll.roundInfo?.scoringType],
            "teams",
          ) &&
            props.simulatorId && (
              <TeamManagement
                scoringType={titleCase(
                  ScoringType[watchAll.roundInfo?.scoringType] ?? "",
                )}
              />
            )}

          {watchAll.roundInfo?.scoringType !== ScoringType.SCRAMBLE &&
            !isLeague && (
              <div className="mt-4 flex flex-row flex-wrap">
                <div className={props.singleColumn ? "w-full" : "w-1/2"}>
                  <SelectFormField<HandicapMode>
                    label="Handicap Mode"
                    options={roundConfigEnumsAsReactSelectOptions.handicapMode}
                    value={
                      watch("roundInfo.handicapMode") ??
                      defaultRoundSettings?.handicapMode
                    }
                    onValueChange={(value) =>
                      setValue("roundInfo.handicapMode", value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    fieldName={"roundInfo.handicapMode"}
                    tooltip="The type of handicap mode to use for the round"
                    className="pr-4"
                    showError={false}
                  />
                </div>
                {watchAll.roundInfo?.handicapMode === HandicapMode.ABSOLUTE ||
                watchAll.roundInfo?.handicapMode === HandicapMode.RELATIVE ||
                defaultRoundSettings?.handicapMode !== HandicapMode.OFF ? (
                  <div className={props.singleColumn ? "w-full" : "w-1/2"}>
                    <SelectFormField<HandicapPercentage>
                      label="Handicap Percentage"
                      options={
                        roundConfigEnumsAsReactSelectOptions.handicapPercentage
                      }
                      value={
                        watch("roundInfo.handicapPercentage") ??
                        roundConfigEnumsAsReactSelectOptions
                          .handicapPercentage?.[0]?.value
                      }
                      onValueChange={(value) =>
                        setValue("roundInfo.handicapPercentage", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                      fieldName={"roundInfo.handicapPercentage"}
                      tooltip="The percentage of the handicap to use for the round"
                    />
                  </div>
                ) : null}
                {watchAll.roundInfo?.handicapMode === HandicapMode.ABSOLUTE ||
                watchAll.roundInfo?.handicapMode === HandicapMode.RELATIVE ||
                defaultRoundSettings?.handicapMode !== HandicapMode.OFF ? (
                  <div className={props.singleColumn ? "w-full" : "w-1/2"}>
                    <SelectFormField<string | number | boolean>
                      label="Course Rating and Slope"
                      options={trueFalseOptions}
                      value={
                        watch("roundInfo.courseRatingSlope") ??
                        trueFalseOptions[0]?.value
                      }
                      onValueChange={(value) =>
                        setValue("roundInfo.courseRatingSlope", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                      fieldName={"roundInfo.courseRatingSlope"}
                      tooltip="Whether to use course rating and slope for the round"
                      className="pr-4 pt-4"
                      showError={false}
                    />
                  </div>
                ) : null}
              </div>
            )}
        </div>
      </div>
      <ReactTooltip place="right" />
    </div>
  );
};

export default RoundSpecificForm;

type RoundSpecificFormProps = {
  simulatorId?: string;
  allowNoPuttMode?: boolean;
  allowPlayerProfileTeeType?: boolean;
  defaultRoundSettingsChecked?: boolean;
  roundConfig?: boolean;
  defaultRoundSettings: DefaultRoundSettings | undefined;
  /**
   * Stack the settings one per row instead of in two columns. For narrow hosts
   * — the league wizard opens this form in a side tray, where two columns of
   * selects are unusably cramped. Off by default, so the full-width callers
   * (Start Game, Create Round, Ad-hoc) keep the layout they have.
   */
  singleColumn?: boolean;
  isLeague?: boolean;
};
